import React, { useState } from 'react';
import { useToast } from './Toast';
import { transcriptionService } from '../services/transcriptionService';

const LOCALSTORAGE_KEY = 'notes_backup';

/**
 * Кнопка AI обработки заметок с возможностью отмены
 */
export function AINotesButton({ 
  notes,
  onNotesChange,
  onNotificationExtracted,  // Новый callback для notification
  disabled = false 
}) {
  const toast = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasBackup, setHasBackup] = useState(false);
  
  /**
   * Обработать заметки через AI
   */
  const handleAIProcess = async () => {
    if (!notes || notes.trim() === '') {
      toast.error('Заметки пустые');
      return;
    }
    
    try {
      setIsProcessing(true);
      // console.log('[AINotesButton] Отправка на AI обработку...');
      
      // Сохраняем оригинальный текст в localStorage
      localStorage.setItem(LOCALSTORAGE_KEY, notes);
      setHasBackup(true);
      
      const result = await transcriptionService.processText(notes);
      
      // console.log('[AINotesButton] AI обработка успешна');
      toast.success('Текст обработан');
      
      // Обновляем заметки
      if (onNotesChange) {
        onNotesChange(result.text);
      }
      
      // Передаем notification если найден
      if (result.notification && onNotificationExtracted) {
        console.log('[AINotesButton] Извлечена notification:', result.notification);
        onNotificationExtracted(result.notification);
        toast.success('Найдена договоренность о встрече! 🔔');
      }
      
    } catch (error) {
      console.error('[AINotesButton] Ошибка AI обработки:', error);
      toast.error('Ошибка обработки текста');
      
      // Удаляем backup при ошибке
      localStorage.removeItem(LOCALSTORAGE_KEY);
      setHasBackup(false);
    } finally {
      setIsProcessing(false);
    }
  };
  
  /**
   * Отменить изменения (вернуть оригинал)
   */
  const handleUndo = () => {
    const backup = localStorage.getItem(LOCALSTORAGE_KEY);
    
    if (backup) {
      // console.log('[AINotesButton] Возврат к оригиналу');
      
      if (onNotesChange) {
        onNotesChange(backup);
      }
      
      localStorage.removeItem(LOCALSTORAGE_KEY);
      setHasBackup(false);
      toast.success('Изменения отменены');
    }
  };
  
  /**
   * Очистить backup (при сохранении формы)
   */
  const clearBackup = () => {
    localStorage.removeItem(LOCALSTORAGE_KEY);
    setHasBackup(false);
  };
  
  // Проверяем наличие backup при монтировании И при изменении notes
  React.useEffect(() => {
    const backup = localStorage.getItem(LOCALSTORAGE_KEY);
    setHasBackup(!!backup);
  }, [notes]); // Добавили зависимость от notes
  
  // Предоставляем метод clearBackup родителю через ref (если нужно)
  React.useImperativeHandle(React.useRef(), () => ({
    clearBackup
  }));
  
  return (
    <div className="flex items-center gap-2">
      {!hasBackup ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleAIProcess();
          }}
          disabled={disabled || isProcessing || !notes || notes.trim() === ''}
          className="px-3 py-1 text-xs rounded-lg border border-purple-200 dark:border-purple-800 bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          title="Обработать текст через AI"
        >
          {isProcessing ? (
            <>
              <span>⏳</span>
              <span>AI...</span>
            </>
          ) : (
            <>
              <span>🤖</span>
              <span>AI</span>
            </>
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleUndo();
          }}
          disabled={disabled}
          className="px-3 py-1 text-xs rounded-lg border border-orange-200 dark:border-orange-800 bg-white dark:bg-gray-800 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          title="Отменить изменения AI"
        >
          <span>↩️</span>
          <span>Отменить</span>
        </button>
      )}
    </div>
  );
}

// Экспортируем метод для очистки backup извне
export function clearNotesBackup() {
  localStorage.removeItem(LOCALSTORAGE_KEY);
}
